import React, { ReactNode, useContext, useState, useEffect } from 'react';
import css from './DocumentHeader.module.css';
import SearchFilter from '../SearchFilter/SearchFilter';
import FileDownloadButton from '../FileDownloadButton/FileDownloadButton';
import FolderDownloadButton from '../FolderDownloadButton/FolderDownloadButton';
import FileUploadButton from '../FileUploadButton/FileUploadButton';
import DeleteButton from '../DeleteButton/DeleteButton';
import { UserContext } from '../../../context/userContext';
import { FaSearch, FaChevronDown, FaChevronUp } from 'react-icons/fa';

interface DocumentHeaderProps {
  currentPath: string;
  filterText: string;
  setFilterText: (text: string) => void;
  selectedFiles: Set<string>;
  onMultipleDownload: () => void;
  showFolderDownload: boolean;
  currentFolderName: string;
  onDownloadEntireFolder: () => void;
  onUploadComplete: () => void;
  onDeleteComplete: () => void;
  children?: ReactNode;
}

const DocumentHeader: React.FC<DocumentHeaderProps> = ({
  currentPath,
  filterText,
  setFilterText,
  selectedFiles,
  onMultipleDownload,
  showFolderDownload,
  currentFolderName,
  onDownloadEntireFolder,
  onUploadComplete,
  onDeleteComplete,
  children
}) => {
  const { checkRole } = useContext(UserContext);
  const isAdminOrManager = checkRole && (
    checkRole('Admin') || 
    checkRole('atlas.admin') || 
    checkRole('Manager') || 
    checkRole('atlas.manager')
  );
  const [showMenu, setShowMenu] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className={css.header}>
      <div className={css.navigationSection}>
        {children}
      </div>
      
      <div className={css.headerControls}>
        <div className={css.mainControls}>
          {/* Desktop: show search input, Mobile: show icon */}
          {!isMobile && (
            <SearchFilter 
              filterText={filterText}
              setFilterText={setFilterText}
              currentPath={currentPath}
            />
          )}
          
          <div className={css.desktopActions} style={{ display: isMobile ? 'none' : undefined }}>
            {isAdminOrManager && (
              <FileUploadButton
                currentPath={currentPath}
                onUploadComplete={onUploadComplete}
              />
            )}
            
            {showFolderDownload && (
              <FolderDownloadButton 
                folderName={currentFolderName}
                onDownload={onDownloadEntireFolder}
              />
            )}
          </div>
        </div>

        <div className={css.selectionControls} style={{ display: isMobile ? 'none' : undefined }}>
          <FileDownloadButton 
            selectedCount={selectedFiles.size}
            onDownload={onMultipleDownload}
          />
          
          {isAdminOrManager && (
            <DeleteButton
              selectedFiles={selectedFiles}
              onDeleteComplete={onDeleteComplete}
            />
          )}
        </div>

        {/* Mobile controls only */}
        {isMobile && (
          <div className={css.mobileActions}>
            {/* Mobile search icon */}
            <button
              className={css.mobileSearchButton}
              onClick={() => setShowMobileSearch((v) => !v)}
              aria-label="Show search"
            >
              <FaSearch />
            </button>
            {/* Mobile search popover/modal */}
            {showMobileSearch && (
              <div className={css.mobileSearchPopover}>
                <SearchFilter
                  filterText={filterText}
                  setFilterText={setFilterText}
                  currentPath={currentPath}
                />
              </div>
            )}
            {/* Actions button with chevron */}
            <button
              className={css.mobileMenuButton}
              onClick={() => setShowMenu(!showMenu)}
              aria-label="Show actions"
            >
              Actions
              <span
                className={css.chevronIcon + ' ' + (showMenu ? css.chevronOpen : '')}
              >
                {showMenu ? <FaChevronUp /> : <FaChevronDown />}
              </span>
            </button>
            {showMenu && (
              <div className={css.dropdownMenu}>
                {isAdminOrManager && (
                  <FileUploadButton
                    currentPath={currentPath}
                    onUploadComplete={onUploadComplete}
                  />
                )}
                {showFolderDownload && (
                  <FolderDownloadButton
                    folderName={currentFolderName}
                    onDownload={onDownloadEntireFolder}
                  />
                )}
                <FileDownloadButton
                  selectedCount={selectedFiles.size}
                  onDownload={onMultipleDownload}
                />
                {isAdminOrManager && (
                  <DeleteButton
                    selectedFiles={selectedFiles}
                    onDeleteComplete={onDeleteComplete}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentHeader;